
  import { Component, Vue, Watch } from 'vue-property-decorator';
  import vSelect from 'vue-select';
  import debounce from 'lodash/debounce';
  import { Validations } from 'vuelidate-property-decorators';
  import { required } from 'vuelidate/lib/validators';

  import { latLng, LatLng } from 'leaflet';
  import {
    Client,
    IClientSearchResultModel,
    ClientSearchResultModel,
    NetworkModel,
    ServiceTypeModel,
    IJobCreateModel,
    JobCreateModel,
    PostcodeModel,
    LocationModel,
    EmployeeModel,
    ValidationProblemDetails,
    ProblemDetails
  } from '@/services/api.service';
  import {
    JobItemType,
    Status,
    JobType,
    AreaType,
    EnumModel,
    StatusEnums,
    JobTypeEnums,
    JobItemTypeEnums,
    AreaTypeEnums
  } from '@/common/enumerations';
  import LocationSelectorMap from '@/components/gis/LocationSelectorMap.vue';
  import * as StringUtils from '@/utilities/string.utils';
  import { filter } from 'vue/types/umd';

  function nonzero(value: any) {
    try {
      const val = parseFloat(value);
      return val > 0;
    } catch (e) {
      return false;
    }
  }
  function zeroorgreater(value: any) {
    try {
      const val = parseFloat(value);
      return val >= 0;
    } catch (e) {
      return false;
    }
  }

  function validarea(value: any) {
    try {
      const val = parseFloat(value);
      return val === 0 || val === 1;
    } catch (e) {
      console.log('error validating area: ' + e);
      return false;
    }
  }

  @Component({
    components: {
      vSelect,
      LocationSelectorMap
    }
  })
  export default class JobCreate extends Vue {
    // job data properties
    private jobName: string = '';
    private networkGid: string = '';
    private clientGid: string = '';
    private jobNumber: string = '';
    private externalReferenceNumber: string = '';
    private workOrderNumber: string = '';
    private jobType: number = -1;
    private initiatorName: string = '';
    private initiatorPhoneNumber: string = '';
    private siteContactName: string = '';
    private siteContactPhoneNumber: string = '';
    private quotedProject: boolean = false;
    private projectDescription: string = '';

    // job item properties
    private itemName: string = '';
    private jobItemType: number = -1;
    private serviceTypeGid: string = '';
    private expectedStartDate: Date | null = null;
    private status: number = 0;
    private isFullDay: boolean = false;
    private purchaseOrderNumber: string = '';
    private taskContactName: string = '';
    private taskContactPhoneNumber: string = '';
    private quoteValue: number = 0;
    private isAdditionalToQuotedProjectScope: boolean = false;

    // job item location properties
    private suburb: string = '';
    private postcode: string = '';
    private address: string = '';
    private gridReference: string = '';
    private locationIdentifier: string = '';
    private latitude: number | null = null;
    private longitude: number | null = null;
    private area: number = -1;

    // job item schedule properties
    private scheduledDate: Date | null = null;
    private scheduledTimeRange: string = '';
    private estimatedDurationInMinutes: number = 0;
    private estimatedTravelTimeInMinutes: number = 0;

    private assignedEmployeeGid: string = '';
    private assignableEmployees: EmployeeModel[] = [];
    private assignableEmployeesPlaceholder: EmployeeModel | null = null;

    // private locations: LocationModel[] = [];

    private clientName: string = '';
    private selectedClient: ClientSearchResultModel | null = null;
    private matchingClients: ClientSearchResultModel[] = [];

    private selectedSuburb: PostcodeModel | null = null;
    private matchingSuburbs: PostcodeModel[] = [];

    private availableNetworks: NetworkModel[] = [];
    private availableServiceTypes: ServiceTypeModel[] = [];
    private filteredServiceTypes: ServiceTypeModel[] = [];
    private availableJobTypes: EnumModel[] = [];
    private availableJobItemTypes: EnumModel[] = [];
    private availableAreaTypes: EnumModel[] = [];
    private availableStatusTypes: EnumModel[] = [];

    private enableServiceTypeSelection: boolean = false;

    private baseJobNumber: number = 0;

    private suppressUpdatePinLocation: boolean = false;

    private jobItemNumber: number = 1;
    private formIsValid: boolean = false;
    private isSubmittingToService: boolean = false;

    @Validations()
    private validations = {
      jobName: { required },
      networkGid: { required },
      selectedClient: { required },
      jobType: { zeroorgreater },
      jobItemType: { nonzero },
      itemName: { required },
      serviceTypeGid: { required },
      suburb: { required },
      postcode: { required },
      area: { validarea }
    };

    private mounted() {
      // get the networks
      // @ts-ignore
      this.$Progress.start();
      this.fetchInitialData();

      const pickJobTypeModel: EnumModel = new EnumModel('(Select Project Type...)', -1);
      this.availableJobTypes.push(
        ...JobTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
      );
      this.availableJobTypes.unshift(pickJobTypeModel);

      const pickJobItemTypeModel: EnumModel = new EnumModel('(Select Job Type...)', -1);
      this.availableJobItemTypes.push(
        ...JobItemTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
      );
      this.availableJobItemTypes.unshift(pickJobItemTypeModel);

      const pickStatusModel: EnumModel = new EnumModel('(Select Status...)', -1);
      this.availableStatusTypes.push(...StatusEnums);
      this.availableStatusTypes.unshift(pickStatusModel);

      const pickAreaTypeModel: EnumModel = new EnumModel('(Select Area...)', -1);
      this.availableAreaTypes.push(
        ...AreaTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
      );
      this.availableAreaTypes.unshift(pickAreaTypeModel);

      const assignableEmployeesPlaceholder = new EmployeeModel();
      assignableEmployeesPlaceholder.init({ name: '(Select a Job Type first)', gid: '' });
      this.assignableEmployees.unshift(assignableEmployeesPlaceholder);
    }

    private createJob() {
      // Run the validation
      this.isSubmittingToService = true;
      // @ts-ignore
      this.$v.$touch();
      // @ts-ignore
      if (this.$v.$invalid) {
        this.formIsValid = false;
        // @ts-ignore
        console.log(this.$v);
        // Show a message
        this.$modal.show('modal-jobcreate-validation-errors');
        this.isSubmittingToService = false;
      } else {
        const newJob: JobCreateModel = new JobCreateModel();
        newJob.init({
          networkGid: this.networkGid,
          jobName: this.jobName,
          clientGid: this.selectedClient ? this.selectedClient.gid : undefined,
          jobType: this.jobType,
          externalReferenceNumber: this.externalReferenceNumber,
          jobNumber: this.jobNumber,
          workOrderNumber: this.workOrderNumber,
          initiatorName: this.initiatorName,
          initiatorPhoneNumber: this.initiatorPhoneNumber,
          siteContactName: this.siteContactName,
          siteContactPhoneNumber: this.siteContactPhoneNumber,
          isQuotedProject: this.quotedProject,
          projectDescription: this.projectDescription,

          itemName: this.itemName,
          jobItemType: this.jobItemType,
          serviceTypeGid: this.serviceTypeGid || undefined,
          status: this.status,
          expectedStartDate: this.expectedStartDate,
          jobItemNumber: this.jobItemNumber,
          isFullDay: this.isFullDay,
          purchaseOrderNumber: this.purchaseOrderNumber,
          contactName: this.taskContactName,
          contactPhoneNumber: this.taskContactPhoneNumber,
          quoteValue: this.quoteValue,
          isAdditionalToQuotedProjectScope: this.isAdditionalToQuotedProjectScope,
          address: this.address,
          suburb: this.suburb,
          postcode: this.postcode,
          gridReference: this.gridReference,
          locationIdentifier: this.locationIdentifier,
          latitude: this.latitude,
          longitude: this.longitude,
          area: this.area,

          scheduledDate: this.scheduledDate,
          scheduledTimeRange: this.scheduledTimeRange,
          estimatedDurationInMinutes: this.estimatedDurationInMinutes,
          estimatedTravelTimeInMinutes: this.estimatedTravelTimeInMinutes,
          assignedToEmployeeGid: this.assignedEmployeeGid || undefined
        });

        const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
        // @ts-ignore
        this.$Progress.start();
        clientService
          .createJob(newJob)
          .then(data => {
            // Route to the edit job list screen?
            // @ts-ignore
            this.$Progress.finish();
            // @ts-ignore
            this.$awn.success('Project ' + this.jobName + ' created successfully');
            // For the moment, nav to the client screen
            this.$router.push({ path: '/jobs/view/' + data.gid });
          })
          .catch(reason => {
            console.log({ reason });

            if (reason instanceof ValidationProblemDetails) {
              const errMsg = 'There were validation issues creating the project: <br/><br/>' + reason.detail;
              // @ts-ignore
              me.$Progress.finish();
              // @ts-ignore
              me.$awn.warning(errMsg, { durations: { warning: 0 } });
              this.isSubmittingToService = false;
            } else if (reason instanceof ProblemDetails) {
              const errMsg = 'Job failed to create: <br/><br/>' + reason.detail;
              // @ts-ignore
              me.$Progress.finish();
              // @ts-ignore
              me.$awn.alert(errMsg);
              this.isSubmittingToService = false;
            }
          });
      }
    }

    private cancelCreateJob() {
      this.$router.push({ path: '/jobs' });
    }

    private updateJobNumber() {
      const matchednetworks = this.availableNetworks.filter(nw => nw.gid === this.networkGid);
      let networkCode: string = 'XX';
      if (matchednetworks !== undefined && matchednetworks.length > 0) {
        const currentNetwork: NetworkModel = matchednetworks[0];
        console.log('updateJobNumber:currentNetwork:');
        console.log(currentNetwork);
        if (currentNetwork.code !== null && currentNetwork.code !== undefined) {
          networkCode = currentNetwork.code;
        }
      }
      this.jobNumber = networkCode + this.baseJobNumber.toString();
    }
    private async fetchInitialData() {
      // Load all the initial data required for this process
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);

      const [networks, serviceTypes, nextJobNumber] = await Promise.all([
        clientService.getNetworks(),
        clientService.getServiceTypes(),
        clientService.getNextJobNumber()
      ]);

      const pickNetworkModel: NetworkModel = new NetworkModel();
      pickNetworkModel.init({ name: '(Select Network...)', gid: '' });
      this.availableNetworks = networks.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''));
      this.availableNetworks.unshift(pickNetworkModel);

      this.availableServiceTypes = serviceTypes.sort((n1, n2) =>
        StringUtils.stringCompare(n1.name || '', n2.name || '')
      );

      this.filterServiceTypes(this.jobItemType);

      this.baseJobNumber = nextJobNumber.number!;
      this.updateJobNumber();

      // @ts-ignore
      this.$Progress.finish();
    }

    private filterServiceTypes(selectedJobItemType: number) {
      // restrict the list of the job item types to
      // only those that match the selected
      if (selectedJobItemType === -1) {
        // the 'Select Job Item Type' option is selected
        this.filteredServiceTypes = [];
        const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
        pickServiceTypeModel.init({ name: '(Select Job Type First...)', gid: '' });
        this.filteredServiceTypes.push(pickServiceTypeModel);
        // force set the selected item back to the picker item
        this.serviceTypeGid = pickServiceTypeModel.gid || '';

        this.enableServiceTypeSelection = false;
      } else {
        // filter and reload the list
        let theFilteredList: ServiceTypeModel[] = [];
        theFilteredList = this.availableServiceTypes.filter(
          st => st.parentJobType === 0 || st.parentJobType === selectedJobItemType
        );
        // Inject the selector item if there are
        // multiple items to show, otherwise default to the
        // first one
        if (theFilteredList.length !== 1) {
          const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
          pickServiceTypeModel.init({ name: '(Select Service Type...)', gid: '' });
          theFilteredList.unshift(pickServiceTypeModel);
        }
        this.filteredServiceTypes = theFilteredList;

        this.enableServiceTypeSelection = true;
      }
    }

    private searchForClients(search: string, loading: (show: boolean) => any) {
      if (search !== undefined && search !== null && search !== '') {
        loading(true);
        this.internalSearchForClients(search, loading, this);
      }
    }

    // Taken from: https://stackoverflow.com/questions/40686767/using-lodash-debounce-in-angular2-typescript-scroll-not-working
    private internalSearchForClients = debounce((search, loading, vm) => {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.searchClients(search).then(data => {
        // push the results into the list
        vm.matchingClients = data.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''));
        loading(false);
      });
    }, 300);

    private searchForSuburb(search: string, loading: (show: boolean) => any) {
      if (search !== undefined && search !== null && search !== '') {
        loading(true);
        this.internalSearchForSuburb(search, loading, this);
      }
    }

    // Taken from: https://stackoverflow.com/questions/40686767/using-lodash-debounce-in-angular2-typescript-scroll-not-working
    private internalSearchForSuburb = debounce((search, loading, vm) => {
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.searchSuburbs(search).then(data => {
        // push the results into the list
        vm.matchingSuburbs = data;
        loading(false);
      });
    }, 300);

    private updateAreaType(postcode: string, suburb: string) {
      console.log('Looking up travel time for ' + postcode + '/' + suburb);
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);
      clientService.lookupTravelTime(suburb, postcode).then(data => {
        // push the results into the list
        if (data) {
          const locatedAreaType = this.availableAreaTypes.find(at => at.value === data.areaType);
          if (locatedAreaType) {
            this.area = locatedAreaType.value;
          }
        }
      });
    }

    private locationPinUpdated(lat: number, lon: number) {
      console.log('New pin location:', latLng(lat, lon));
      if (!this.suppressUpdatePinLocation) {
        this.latitude = lat;
        this.longitude = lon;
      }
    }

    // private updatePinLocation() {
    //   if (!this.suppressUpdatePinLocation) {

    //   }
    // }

    private get pinLatitude() {
      if (this.latitude === null) {
        return 0;
      }
      return this.latitude!;
    }

    private get pinLongitude() {
      if (this.longitude === null) {
        return 0;
      }
      return this.longitude!;
    }

    @Watch('networkGid')
    private onNetworkGidChanged(value: string, oldValue: string) {
      // does not require underlying data set to change
      this.updateJobNumber();
    }

    // @Watch('latitude')
    // private onLatitudeChanged(value: number, oldValue: number) {
    //   // does not require underlying data set to change
    //   this.updatePinLocation();
    // }

    // @Watch('longitude')
    // private onLongitudeChanged(value: number, oldValue: number) {
    //   // does not require underlying data set to change
    //   this.updatePinLocation();
    // }

    @Watch('selectedSuburb')
    private onSelectedSuburbChanged(value: PostcodeModel, oldValue: PostcodeModel) {
      console.log('Suburb changed');

      if (value !== null) {
        this.suburb = value.suburb!;
        let thePC = value.postcode!.toString();
        if (thePC.length === 3) {
          thePC = '0' + thePC;
        }

        // this.suppressUpdatePinLocation = true;
        this.postcode = thePC;

        if (this.postcode && this.suburb) {
          // auto fill the urban/regional state
          this.updateAreaType(this.postcode, (this.selectedSuburb && this.suburb) || '');
        }

        if ((this.latitude === null || this.latitude === 0) && (this.longitude === null || this.longitude === 0)) {
          this.suppressUpdatePinLocation = true;
          this.latitude = value.latitude!;
          this.longitude = value.longitude!;

          this.$nextTick(() => (this.suppressUpdatePinLocation = false));
        }
        // this.suppressUpdatePinLocation = false;
        // this.updatePinLocation();
      }
    }

    @Watch('postcode')
    private onPostcodeChanged(value: string, oldValue: string) {
      console.log('Postcode changed');

      if (this.postcode && this.suburb) {
        // auto fill the urban/regional state
        this.updateAreaType(this.postcode, (this.selectedSuburb && this.suburb) || '');
      }
    }
    // if status is pending and schedule value has been entered, switch status to  'scheduled'
    @Watch('scheduledDate')
    private onScheduledDateChanged(value: Date | null, oldValue: Date | null) {
      if (value != null && oldValue == null && this.status === 0) {
        this.status = 1;
      }
    }

    // if the item name has not been manually set, generate a name based on the selected serviceType
    @Watch('serviceTypeGid')
    private onServiceTypeGidChanged(value: string, oldValue: string) {
      const serviceType = this.availableServiceTypes.find(t => t.gid === value);

      if (value == null || serviceType == null) {
        return;
      }

      if (oldValue === '') {
        this.itemName = serviceType.name!;
      } else {
        const oldServiceType = this.availableServiceTypes.find(t => t.gid === oldValue);

        if (oldServiceType == null) {
          this.itemName = serviceType.name!;
        } else if (this.itemName === oldServiceType.name) {
          this.itemName = serviceType.name!;
        }
      }
    }

    @Watch('jobItemType')
    private onjobTypeChanged(value: number, oldValue: number) {
      this.filterServiceTypes(value);
      this.updateAssignableEmployees(value);
    }

    private updateAssignableEmployees(jobItemType: JobItemType) {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

      const employees = clientService.getAssignableEmployees(jobItemType as any).then(data => {
        const sortedData = data.sort((n1, n2) => StringUtils.stringCompare(n1.firstName || '', n2.firstName || ''));
        const assignableEmployeesPlaceholder = new EmployeeModel();
        assignableEmployeesPlaceholder.init({ firstName: '(Select Employee...)', lastName: '', gid: '' });
        sortedData.unshift(assignableEmployeesPlaceholder);

        this.assignableEmployees = sortedData;
      });
    }
  }
